import type { TFunction } from 'i18next';

import {
  collectChips,
  dateRangeChip,
  getGroupByOptions,
  getMetricOptions,
  optionChips,
  subsetChip,
  type SelectionOption
} from '@/features/dashboard-filters';
import type { ActiveFilterChip } from '@/features/header-filters';

import type { StorageUrlState } from './types';

interface BuildStorageFilterChipsArgs {
  state: StorageUrlState;
  defaults: StorageUrlState;
  t: TFunction;
  locations: SelectionOption[];
  selectedLocations: string[];
  /** True while the date range is on offer, i.e. on the Stock movements tab. */
  showDateRange: boolean;
  onRemove: (key: string) => void;
  onResetLocations: () => void;
}

/**
 * Removable chips for the storage dashboard, in the order the panel lists the
 * filters. The tab is not among them: it picks which chart is on screen, not
 * which rows the chart is drawn from.
 *
 * Pure, so the accounting can be tested without the hook or the API.
 */
export function buildStorageFilterChips({
  state,
  defaults,
  t,
  locations,
  selectedLocations,
  showDateRange,
  onRemove,
  onResetLocations
}: BuildStorageFilterChipsArgs): ActiveFilterChip[] {
  const chip = optionChips(state, defaults, onRemove);

  return collectChips(
    subsetChip({
      key: 'locations',
      title: t('dashboard.locations.label', 'Locations'),
      selected: selectedLocations,
      available: locations,
      onRemove: onResetLocations
    }),
    chip('metric', getMetricOptions(t)),
    chip('group', getGroupByOptions(t)),
    // A chip for a control the panel will not show can only be deleted, never corrected.
    showDateRange &&
      dateRangeChip({
        period: state.period,
        fromDate: state.fromDate,
        toDate: state.toDate,
        defaultPeriod: defaults.period,
        t,
        onRemove
      })
  );
}
